/**
 * Los sistemas de origen de una cuenta: su lead en Close, su customer y su
 * suscripción en Stripe, y su ficha en la plataforma.
 *
 * Todo pasa por `ExternalLink`, que es el único componente autorizado a escribir
 * un `<a target="_blank">`: de ahí salen la pestaña nueva, el `rel` y el aviso
 * accesible del salto. Cuando el id externo falta, el link **no se renderiza**:
 * aparece "sin vincular", igual que un KPI sin dato.
 *
 * Los links de Stripe son solo para admin. Un deep link no es una cifra, pero
 * abre la ficha de facturación de la cuenta, así que sigue la misma regla que la
 * columna de plata.
 */
import type { ClientLinks } from '@ops/contracts'

import { ExternalLink } from '../../ui'

/** Qué sistema se vincula, para el título del seguimiento que lo pide. */
export type SistemaVinculable =
  | 'el lead en Close'
  | 'el customer en Stripe'
  | 'la suscripción en Stripe'
  | 'la ficha en la plataforma'

export interface SistemasDeOrigenProps {
  /** El sobre de links de esta cuenta. `undefined` = la API no lo mandó. */
  links: ClientLinks | undefined
  /** Nombre de la cuenta, para el texto de la acción de vincular. */
  nombre: string
  verPlata: boolean
  /** `fila` para una celda de tabla; `columna` para la ficha. */
  orientacion?: 'fila' | 'columna'
  /** Con la suscripción además del customer. Solo en la ficha. */
  conSuscripcion?: boolean
  /** Sin handler el estado "sin vincular" no ofrece botón. */
  onVincular?: (sistema: SistemaVinculable) => void
}

interface Entrada {
  clave: string
  etiqueta: string
  sistema: SistemaVinculable
  link: ClientLinks['close']
}

export function SistemasDeOrigen({
  links,
  nombre,
  verPlata,
  orientacion = 'fila',
  conSuscripcion = false,
  onVincular,
}: SistemasDeOrigenProps) {
  const entradas: Entrada[] = [
    {
      clave: 'close',
      etiqueta: 'Close',
      sistema: 'el lead en Close',
      link: links?.close ?? null,
    },
  ]

  if (verPlata) {
    entradas.push({
      clave: 'stripe',
      etiqueta: 'Stripe',
      sistema: 'el customer en Stripe',
      link: links?.stripeCustomer ?? null,
    })
    if (conSuscripcion) {
      entradas.push({
        clave: 'stripe-sub',
        etiqueta: 'Suscripción',
        sistema: 'la suscripción en Stripe',
        link: links?.stripeSubscription ?? null,
      })
    }
  }

  entradas.push({
    clave: 'plataforma',
    etiqueta: 'Plataforma',
    sistema: 'la ficha en la plataforma',
    link: links?.plataforma ?? null,
  })

  if (orientacion === 'fila') {
    return (
      <span
        data-testid="sistemas-de-origen"
        className="inline-flex flex-wrap items-center gap-1.5"
      >
        {entradas.map((entrada) => (
          <ExternalLink
            key={entrada.clave}
            link={entrada.link}
            variante="chip"
            sistemaFaltante={entrada.etiqueta}
            /*
             * En la celda de la tabla el botón entra igual: `variante="chico"`
             * mide 20px de alto y el estado ya viene con `flex-wrap`. El texto
             * es corto por eso — en la ficha, donde hay ancho, dice la frase
             * entera ("Pedir que se vincule").
             */
            onVincular={
              onVincular === undefined
                ? undefined
                : () => onVincular(entrada.sistema)
            }
            textoVincular="Vincular"
          >
            {entrada.etiqueta}
          </ExternalLink>
        ))}
      </span>
    )
  }

  return (
    <ul
      data-testid="sistemas-de-origen"
      className="flex list-none flex-col gap-1 p-0"
    >
      {entradas.map((entrada) => (
        <li key={entrada.clave} className="flex flex-wrap items-center gap-2">
          <span className="rotulo w-24 shrink-0">{entrada.etiqueta}</span>
          <ExternalLink
            link={entrada.link}
            variante="chip"
            sistemaFaltante={entrada.etiqueta}
            onVincular={
              onVincular === undefined
                ? undefined
                : () => onVincular(entrada.sistema)
            }
            textoVincular="Pedir que se vincule"
          >
            {entrada.link?.label ?? nombre}
          </ExternalLink>
        </li>
      ))}
    </ul>
  )
}
